/**
 * Push Notification Handler for Capacitor
 * Uses Firebase Cloud Messaging (FCM) for both iOS and Android
 */

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { supabase } from './supabase';
import { logger } from './logger';

let pushInitialized = false;

/**
 * Check if push notifications are supported on this platform
 */
export const isPushSupported = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Request push notification permissions
 * @returns {Promise<boolean>} Whether permission was granted
 */
export const requestPushPermission = async () => {
  if (!isPushSupported()) {
    logger.info('Push notifications not supported on this platform');
    return false;
  }

  try {
    const permission = await PushNotifications.requestPermissions();

    if (permission.receive === 'granted') {
      logger.info('Push notification permission granted');
      return true;
    } else {
      logger.warn('Push notification permission denied', { status: permission.receive });
      return false;
    }
  } catch (error) {
    logger.error('Error requesting push permission', error);
    return false;
  }
};

/**
 * Save push token to Supabase
 * @param {string} token - FCM token
 */
const savePushToken = async (token) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      logger.warn('Cannot save push token: user not authenticated');
      return;
    }

    const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

    const { error } = await supabase
      .from('mellow_push_tokens')
      .upsert(
        {
          user_id: user.id,
          token,
          platform,
          updated_at: new Date().toISOString()
        },
        {
          onConflict: 'user_id,token'
        }
      );

    if (error) {
      logger.error('Error saving push token', error);
    } else {
      logger.info('Push token saved successfully', { platform });
    }
  } catch (error) {
    logger.error('Error saving push token', error);
  }
};

/**
 * Remove push token from Supabase (on logout)
 * @param {string} token - FCM token to remove
 */
export const removePushToken = async (token) => {
  try {
    const { error } = await supabase
      .from('mellow_push_tokens')
      .delete()
      .eq('token', token);

    if (error) {
      logger.error('Error removing push token', error);
    } else {
      logger.info('Push token removed successfully');
    }
  } catch (error) {
    logger.error('Error removing push token', error);
  }
};

/**
 * Handle notification tap action
 * @param {Object} notification - Notification data
 */
const handleNotificationAction = (notification) => {
  const data = notification.notification?.data || notification.data || {};

  logger.info('Notification action performed', { data });

  // Handle different notification types
  if (data.type === 'streak_reminder') {
    // Navigate to home/discovery
    window.location.href = '/';
  } else if (data.type === 'xp_milestone') {
    // Navigate to profile
    window.location.href = '/profile';
  } else if (data.type === 'new_content') {
    // Navigate to discovery
    window.location.href = '/';
  } else if (data.chatId) {
    // Navigate to specific chat
    window.location.href = `/chat/${data.chatId}`;
  }
};

/**
 * Initialize push notifications
 * Call this after user is authenticated
 */
export const initPushNotifications = async () => {
  if (!isPushSupported()) {
    logger.info('Push notifications not supported on this platform');
    return false;
  }

  if (pushInitialized) {
    logger.info('Push notifications already initialized');
    return true;
  }

  try {
    // Request permission first
    const hasPermission = await requestPushPermission();
    if (!hasPermission) {
      return false;
    }

    // Register for push notifications
    await PushNotifications.register();

    // Handle registration success
    PushNotifications.addListener('registration', async (token) => {
      logger.info('Push registration successful', { token: token.value.substring(0, 20) + '...' });
      await savePushToken(token.value);
    });

    // Handle registration error
    PushNotifications.addListener('registrationError', (error) => {
      logger.error('Push registration failed', error);
    });

    // Handle foreground notifications
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      logger.info('Push notification received in foreground', {
        title: notification.title,
        body: notification.body
      });
      // Optionally show in-app notification UI here
    });

    // Handle notification tap
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      logger.info('Push notification action performed');
      handleNotificationAction(action);
    });

    pushInitialized = true;
    logger.info('Push notifications initialized successfully');
    return true;
  } catch (error) {
    logger.error('Error initializing push notifications', error);
    return false;
  }
};

/**
 * Check current permission status
 * @returns {Promise<string>} Permission status: 'granted' | 'denied' | 'prompt'
 */
export const getPushPermissionStatus = async () => {
  if (!isPushSupported()) {
    return 'denied';
  }

  try {
    const permission = await PushNotifications.checkPermissions();
    return permission.receive;
  } catch (error) {
    logger.error('Error checking push permission', error);
    return 'denied';
  }
};
